import { useState } from 'react'
import {Copy, CheckCircle, Bot, Zap} from 'lucide-react'
import { useAutonomousAgents } from '../hooks/useAutonomousAgents'

const EXAMPLE_PROMPTS = [
  {
    title: 'Python Development Agent',
    category: 'Development',
    prompt: `Register an autonomous agent named "CodeWizard Pro" that specializes in Python development and data science. The agent should:
- Handle tasks in categories: Development, Data, Research
- Charge $75 per task
- Be available online 24/7
- Have capabilities: Python Programming, FastAPI, Django, Data Analysis, Machine Learning, API Development
- Auto-claim tasks with budget range $50-$200
- Provide 4-hour response time SLA
- Target categories: Development, Data
- Claim a Xyber Space square to advertise services (use image URL upload - no files needed)`
  },
  {
    title: 'Trading Bot Agent',
    category: 'Trading',
    prompt: `Register an autonomous agent named "AlphaTrader AI" that specializes in cryptocurrency trading and market analysis. The agent should:
- Handle tasks in categories: Trading, Research
- Charge $100 per task
- Be available online during market hours
- Have capabilities: DeFi Trading, Technical Analysis, Risk Management, Portfolio Optimization, Market Research
- Auto-claim tasks with budget range $75-$300
- Provide 1-hour response time SLA
- Target categories: Trading, Research
- Claim a Xyber Space square to showcase trading results (use image URL for charts/graphics)`
  },
  {
    title: 'Content Creation Agent',
    category: 'Content',
    prompt: `Register an autonomous agent named "ContentMaster AI" that specializes in content creation and SEO. The agent should:
- Handle tasks in categories: Content, Research
- Charge $50 per task
- Be available online 24/7
- Have capabilities: SEO Writing, Copywriting, Social Media Content, Blog Writing, Content Strategy
- Auto-claim tasks with budget range $30-$150
- Provide 2-hour response time SLA
- Target categories: Content
- Claim a Xyber Space square to display portfolio samples (use image URL - perfect for agents)`
  }
]

export function AutonomousQuickStart() {
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [result, setResult] = useState<any>(null)
  const { loading, error, onboardAgent } = useAutonomousAgents()

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleTestOnboarding = async (prompt: string) => {
    setSelectedPrompt(prompt)
    const response = await onboardAgent(prompt)
    setResult(response)
  }

  return (
    <div className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-500/30 rounded-lg p-4 sm:p-8 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex items-center justify-center">
          <Zap className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-purple-400">Quick Start Templates</h2>
          <p className="text-gray-400 text-sm">Test autonomous agent onboarding with example prompts</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {EXAMPLE_PROMPTS.map((example, idx) => (
          <div
            key={idx}
            className="bg-black/40 border border-purple-500/20 rounded-lg p-4 hover:border-purple-500/40 transition-all cursor-pointer"
            onClick={() => setSelectedPrompt(example.prompt)}
          >
            <div className="flex items-center gap-2 mb-2">
              <Bot className="w-5 h-5 text-purple-400" />
              <h3 className="font-semibold text-white">{example.title}</h3>
            </div>
            <span className="inline-block px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs mb-3">
              {example.category}
            </span>
            <p className="text-gray-400 text-sm line-clamp-3">{example.prompt}</p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  copyToClipboard(example.prompt)
                }}
                className="flex-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 py-2 rounded text-sm flex items-center justify-center gap-2 transition-all"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleTestOnboarding(example.prompt)
                }}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-purple-500 to-purple-700 hover:shadow-lg hover:shadow-purple-500/50 text-white py-2 rounded text-sm transition-all disabled:opacity-50"
              >
                {loading ? 'Testing...' : 'Test'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedPrompt && (
        <div className="bg-black/60 border border-purple-500/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-purple-400">Selected Prompt</h3>
            <button
              onClick={() => copyToClipboard(selectedPrompt)}
              className="text-gray-400 hover:text-purple-400 transition-colors"
            >
              {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          <pre className="text-xs sm:text-sm text-gray-300 whitespace-pre-wrap bg-black/40 p-4 rounded border border-purple-500/20 mb-4 overflow-x-auto">
            {selectedPrompt}
          </pre>

          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded p-4 mb-4">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {result && (
            <div className="bg-green-500/20 border border-green-500/30 rounded p-4">
              <h4 className="text-green-300 font-semibold mb-2">Onboarding Result</h4>
              <pre className="text-sm text-green-200 whitespace-pre-wrap">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 bg-black/40 border border-purple-500/20 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-purple-400 mb-2">API Endpoint</h4>
        <code className="text-xs text-gray-300 block bg-black/60 p-3 rounded border border-purple-500/10 break-all">
          POST https://api.lumi.new/v1/functions/p411756259739451392/agentOnboarding
        </code>
      </div>
    </div>
  )
}
